import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, setToken } from "@/api/client";
import { validateResponse } from "@/api/validateResponse";
import { LoginResponseSchema, type TwoFactorLoginRequest, type User } from "@/api/types/auth";
import {
  TwoFactorCodeRequestSchema,
  TwoFactorEnrollResponseSchema,
  TwoFactorStatusSchema,
  type TwoFactorCodeRequest,
  type TwoFactorEnrollResponse,
  type TwoFactorStatus,
} from "@/api/types/twoFactor";

const TWO_FA_STATUS_KEY = ["auth", "2fa", "status"] as const;

/** GET /v2/auth/2fa/status — the security settings page's source of truth. */
export function useTwoFactorStatus() {
  return useQuery<TwoFactorStatus>({
    queryKey: TWO_FA_STATUS_KEY,
    queryFn: async () => {
      const { data } = await apiClient.get("/v2/auth/2fa/status");
      return validateResponse(TwoFactorStatusSchema, data, "GET /v2/auth/2fa/status");
    },
    staleTime: 60 * 1000,
  });
}

/** POST /v2/auth/2fa/enroll — generates (and stores, unverified) a new
 * secret; returns it plus the otpauth:// URI for a QR/manual-entry step. */
export function useTwoFactorEnroll() {
  return useMutation<TwoFactorEnrollResponse, unknown, void>({
    mutationFn: async () => {
      const { data } = await apiClient.post("/v2/auth/2fa/enroll");
      return validateResponse(TwoFactorEnrollResponseSchema, data, "POST /v2/auth/2fa/enroll");
    },
  });
}

/** POST /v2/auth/2fa/verify — confirms the code, flips totp_enabled=true. */
export function useTwoFactorVerify() {
  const queryClient = useQueryClient();
  return useMutation<TwoFactorStatus, unknown, TwoFactorCodeRequest>({
    mutationFn: async (payload) => {
      TwoFactorCodeRequestSchema.parse(payload);
      const { data } = await apiClient.post("/v2/auth/2fa/verify", payload);
      return validateResponse(TwoFactorStatusSchema, data, "POST /v2/auth/2fa/verify");
    },
    onSuccess: (status) => {
      queryClient.setQueryData(TWO_FA_STATUS_KEY, status);
    },
  });
}

/** POST /v2/auth/2fa/disable — requires one last valid code. */
export function useTwoFactorDisable() {
  const queryClient = useQueryClient();
  return useMutation<TwoFactorStatus, unknown, TwoFactorCodeRequest>({
    mutationFn: async (payload) => {
      TwoFactorCodeRequestSchema.parse(payload);
      const { data } = await apiClient.post("/v2/auth/2fa/disable", payload);
      return validateResponse(TwoFactorStatusSchema, data, "POST /v2/auth/2fa/disable");
    },
    onSuccess: (status) => {
      queryClient.setQueryData(TWO_FA_STATUS_KEY, status);
    },
  });
}

/** POST /v2/auth/2fa/login — finishes the challenge from useLogin's
 * totpRequired branch; issues a real token exactly like a normal login. */
export function useTwoFactorLogin() {
  const queryClient = useQueryClient();
  return useMutation<User, unknown, TwoFactorLoginRequest>({
    mutationFn: async (payload) => {
      const { data } = await apiClient.post("/v2/auth/2fa/login", payload);
      const parsed = validateResponse(LoginResponseSchema, data, "POST /v2/auth/2fa/login");
      setToken(parsed.access_token);
      return parsed.user;
    },
    onSuccess: (user) => {
      queryClient.setQueryData(["auth", "me"], user);
    },
  });
}
